<script lang="ts" setup>
import { reactive, ref, computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength } from '@vuelidate/validators'
import { Spin } from 'ant-design-vue'
import BsButton from '@/legacy/components/Button.vue'
import BsDialogFull from '@/legacy/components/DialogFull.vue'
import { createCustomEvent } from '@/assets/ts/customEvent'
import useHashQuery from '@/legacy/ts/useHashQuery'
import { instanceBuyandship } from '@/assets/ts/apiManagement'

defineProps({
	text: {
		type: Object,
	},
})

const state = reactive({
	password: '',
	confirmPassword: '',
})

const isShowPanel = ref(true)
const isLoading = ref(false)
const errorMessage = ref('')
const step = ref(1)

const rules = {
	password: {
		required,
		minLength: minLength(6),
	},
	confirmPassword: {
		required,
		minLength: minLength(6),
	},
}

const { searchParameter } = useHashQuery()
const $v = useVuelidate(rules, state)

const email = computed(() => {
	const email = searchParameter.value.find(query => query.key === 'email')
	return email === undefined ? '' : decodeURIComponent(email.val)
})

const token = computed(() => {
	const token = searchParameter.value.find(query => query.key === 'verify')
	return token === undefined ? '' : token.val
})

const isPasswordSame = computed(() => {
	if (state.password.length < 6 || state.confirmPassword.length < 6) {
		return true
	}
	return state.password === state.confirmPassword
})

function showPanel() {
	isShowPanel.value = true
}

function hidePanel(action: string | null) {
	if (action === 'ok')
		createCustomEvent('member_reset_password_completed_ok', null)
	window.history.pushState(
		{ path: window.location.origin },
		'',
		window.location.origin,
	)
	const payload = {
		type: 'hide',
		data: 'resetPassword',
	}
	window.postMessage(payload, window.location.origin)
}

function openLoginPanel() {
	const payload = {
		type: 'show',
		data: 'login',
	}
	window.postMessage(payload, window.location.origin)
}

function resetPassword() {
	if ($v.value.$invalid) return
	if (!isPasswordSame.value) return

	if (email.value === '' || token.value === '') {
		errorMessage.value = 'your link have some problem'
		return
	}

	isLoading.value = true

	instanceBuyandship
		.post('users/reset-password', {
			new_password: state.password,
			email: email.value,
			token: token.value,
		})
		.then(() => {
			if (step.value === 1) {
				step.value += 1
			}
			errorMessage.value = ''
			createCustomEvent('member_reset_password_completed', null)
		})
		.catch(err => {
			let message = 'System is busy, please try later'
			if (
				err.response &&
				err.response.data &&
				err.response.data.error &&
				err.response.data.error.message
			) {
				message = err.response.data.error.message
			}
			errorMessage.value = message
		})
		.finally(() => (isLoading.value = false))
}
</script>

<template>
	<bs-dialog-full
		v-model="isShowPanel"
		class="resetpassword"
		@on-hide="hidePanel(null)"
	>
		<template #content>
			<div class="bs-dialog-full-slider-container">
				<transition name="dialog-slide">
					<div v-show="step === 1" class="resetpassword-container" key="step1">
						<div class="resetpassword-title text-h5">{{ text?.title }}</div>
						<div class="bs-field-group">
							<input
								class="bs-field"
								:class="{ 'bs-field--error': $v.password.$error }"
								v-model="$v.password.$model"
								type="password"
								:placeholder="text?.password.placeholder"
							/>
							<span
								class="bs-field__message"
								:class="{
									'bs-field__message--error': $v.password.required.$invalid,
								}"
								v-if="$v.password.required.$invalid"
							>
								{{ text?.password.required }}
							</span>
							<span
								class="bs-field__message"
								:class="{
									'bs-field__message--error': $v.password.minLength.$invalid,
								}"
								v-if="$v.password.minLength.$invalid"
							>
								{{ text?.password.min }}
							</span>
						</div>
						<div class="bs-field-group">
							<input
								class="bs-field"
								:class="{
									'bs-field--error':
										$v.confirmPassword.$error || !isPasswordSame,
								}"
								v-model="$v.confirmPassword.$model"
								type="password"
								:placeholder="text?.confirm.placeholder"
							/>
							<span
								class="bs-field__message"
								:class="{
									'bs-field__message--error':
										$v.confirmPassword.required.$invalid,
								}"
								v-if="$v.confirmPassword.required.$invalid"
							>
								{{ text?.confirm.required }}
							</span>
							<span
								class="bs-field__message"
								:class="{
									'bs-field__message--error':
										$v.confirmPassword.minLength.$invalid,
								}"
								v-if="$v.confirmPassword.minLength.$invalid"
							>
								{{ text?.confirm.min }}
							</span>
							<span
								class="bs-field__message"
								:class="{ 'bs-field__message--error': !isPasswordSame }"
								v-if="!isPasswordSame"
							>
								{{ text?.confirm.same }}
							</span>
						</div>
						<div class="forgetpassword-action">
							<Spin :spinning="isLoading">
								<bs-button
									class="full-width"
									:class="{
										'bs-button--disabled': $v.$invalid || !isPasswordSame,
									}"
									type="primary"
									solid
									@click="resetPassword"
								>
									{{ text?.button.confirm }}
								</bs-button>
							</Spin>
							<div class="forgetpassword-action-message" v-show="errorMessage">
								{{ errorMessage }}
							</div>
						</div>
					</div>
				</transition>
				<transition name="dialog-slide">
					<div
						v-show="step === 2"
						class="resetpassword-container resetpassword-container--step2"
						key="step2"
					>
						<div class="resetpassword-step2-media">
							<div class="resetpassword-step2-media__round">
								<i class="icon-done-all"></i>
							</div>
						</div>
						<div class="resetpassword-step2-title text-h5">
							{{ text?.success.title }}
						</div>
						<div class="resetpassword-step2-desc">
							{{ text?.success.description }}
						</div>
						<bs-button
							class="resetpassword-finish-button"
							type="primary"
							outline
							@click="hidePanel('ok')"
						>
							{{ text?.button.ok }}
						</bs-button>
					</div>
				</transition>
			</div>
		</template>
	</bs-dialog-full>
</template>
@/legacy/ts/useHashQuery
